// Device detection utilities for PWA features

/**
 * Detects if the user is on an iOS device (iPhone, iPad, iPod)
 */
export const isIOS = (): boolean => {
  if (typeof window === 'undefined') return false;

  const userAgent = window.navigator.userAgent.toLowerCase();
  return /iphone|ipad|ipod/.test(userAgent);
};

/**
 * Detects if the user is on Safari browser
 */
export const isSafari = (): boolean => {
  if (typeof window === 'undefined') return false;

  const userAgent = window.navigator.userAgent.toLowerCase();
  const isSafariBrowser = /safari/.test(userAgent) && !/chrome/.test(userAgent);
  return isSafariBrowser;
};

/**
 * Detects if the app is already installed (running in standalone mode)
 */
export const isStandalone = (): boolean => {
  if (typeof window === 'undefined') return false;

  // Check if running in standalone mode (PWA installed)
  return (
    window.matchMedia('(display-mode: standalone)').matches ||
    (window.navigator as any).standalone === true ||
    document.referrer.includes('android-app://')
  );
};

/**
 * Detects if the device supports PWA installation
 */
export const canInstallPWA = (): boolean => {
  if (typeof window === 'undefined') return false;

  // Check if beforeinstallprompt event is supported (Android Chrome, Edge, etc.)
  return 'BeforeInstallPromptEvent' in window || 'onbeforeinstallprompt' in window;
};

/**
 * Check if the user should see the iOS install banner
 */
export const shouldShowIOSBanner = (): boolean => {
  return isIOS() && isSafari() && !isStandalone();
};

/**
 * Detects if the user is on a mobile device (smartphone or tablet)
 */
export const isMobile = (): boolean => {
  if (typeof window === 'undefined') return false;

  const userAgent = window.navigator.userAgent.toLowerCase();

  // Check for mobile devices
  const mobileRegex = /android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini/i;

  // Also check for touch support and small screen
  const hasTouch = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
  const isSmallScreen = window.innerWidth <= 768;

  return mobileRegex.test(userAgent) || (hasTouch && isSmallScreen);
};
